import React, { ChangeEvent, useCallback, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { Modal } from '../common/Modal';
import { useTheme } from '@mui/material/styles';
import { useNotification } from '../../+state/useNotification';
import { ValidationErrorState } from '../../types/validationErrorState';
import { useMutation } from '@tanstack/react-query';
import { AdminAuthService } from '../../services/admin-auth';
import { TEXTS } from '../../core/texts';
import { AxiosErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';
import { Validator } from '../../core/Validator';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { PersonAdd } from '@mui/icons-material';
import { FlexBoxFullWidth } from '../common/boxes';
import { CancelButton } from '../common/CancelButton';

const AddAdmin = () => {
  const [openedModal, setOpenedModal] = useState(false);
  const theme = useTheme();
  const { openErrorToast } = useNotification();
  const [email, setEmail] = useState('');
  const [responseError, setResponseError] = useState<string | null>(null);
  const [validError, setValidError] = useState<ValidationErrorState>({});
  const [guideMsg, setGuideMsg] = useState<undefined | string>(undefined);

  const toggleModal = useCallback(() => setOpenedModal(prev => {
    if (prev) {
      setValidError({});
    }
    return !prev;
  }), []);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  }, []);

  const mutation = useMutation({
    mutationFn: (email: string) => AdminAuthService.initAdmin(email),
    onSuccess: () => setGuideMsg(TEXTS.ACTIVATION_EMAIL_SENT),
    onError: (err: AxiosErrorResponse) => {
      const msg = parseAxiosError(err);
      setResponseError(msg);
      openErrorToast(msg);
    }
  });

  const submitFormHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setResponseError(null);
    const validationErrors = Validator.validate({ email }, Validator.NAMES.AUTH);
    if (validationErrors) {
      return setValidError(validationErrors);
    }
    mutation.mutate(email);
  }, [email, mutation]);

  return (
    <Box>
      <Button variant={'outlined'} onClick={toggleModal}>{TEXTS.CREATE_ADMIN}</Button>
      {
        openedModal &&
        <Modal onClose={toggleModal} maxWidth={500} withDividers={false} title={TEXTS.CREATE_ADMIN}
               headerIcon={<PersonAdd color={'primary'}/>}
        >
          {
            guideMsg
              ? <Box>
                <Typography variant={'h5'}>
                  {TEXTS.ACTIVATION_EMAIL_SENT}
                </Typography>
                <Box mt={2}/>
                <Typography>
                  {TEXTS.RECOVER_EMAIL_SENT_DETAILS}
                </Typography>
              </Box>
              : <>
                <form onSubmit={submitFormHandler}>
                  <TextField sx={{ my: 2 }} onChange={handleInputChange} name={'email'} placeholder={TEXTS.EMAIL}
                             type={'email'} required={true} error={!!validError['email']}
                             helperText={validError['email']} fullWidth
                  />
                  <FlexBoxFullWidth justifyContent={'space-between'} mt={2}>
                    <CancelButton onClick={toggleModal}/>
                    <ButtonWithLoader type={'submit'} color={'primary'} variant={'outlined'}
                                      loading={mutation.isLoading}>
                      {TEXTS.CONFIRM}
                    </ButtonWithLoader>
                  </FlexBoxFullWidth>
                </form>
                {
                  responseError &&
                  <Typography sx={{ color: theme.palette.error.main, my: 2 }}>
                    {responseError}
                  </Typography>
                }
              </>
          }
        </Modal>
      }
    </Box>
  );
};

export default AddAdmin;